import React from 'react'
import { useParams, useNavigate, Routes, Route } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useSideEffect } from '../../data/store/Effects/side-effects'
import { creditApplicationEffects, creditApplicationSelectors } from '../../data/store/CreditApplicationStore'
import { useAppSelector } from '../../data/store'
import { Enum } from '../../data/types/constants'
import PrequalificationDecisionContainer from './PrequalificationDecisionContainer'
import FinalDecisionContainer from './FinalDecisionContainer'
import BankAccountInformationForm from './BankAccountInformationForm'
import FailedToObtainCreditReportForm from './FailedToObtainCreditReportForm'
import CreditApplicationCancelled from './CreditApplicationCancelled'
import FeedBackPage from './FeedBackPage'
import Worksheet from './WorksheetPage'
import Loader from '../components/Loader'
import { creditApplicationContainerSelection } from '../../data/creditApplicationContainerSelection'
import CreditReportError from './CreditReportError'
import CustomerFeedbackPage from '../CustomerFeedback/CustomerFeedbackPage'
import BadReviewPage from './BadReviewPage'
import FeedBackReviewPage from './FeedBackReviewPage'
import CreditApplicationCompleted from './CreditApplicationCompleted'

export default function PollingCreditApplicationPage() {
  const MAX_RETRIES = 13
  const { t } = useTranslation()
  const { id } = useParams()
  const navigate = useNavigate()
  const dispatchEffect = useSideEffect()
  const creditApplication = useAppSelector(creditApplicationSelectors.getFilteredCreditApplication)
  const customerFeedback = useAppSelector(creditApplicationSelectors.getCustomerFeedback)

  const [retry, setRetry] = React.useState<number>(0)
  const isPollingCreditApplication = React.useRef<boolean>(false)

  React.useEffect(() => {
    dispatchEffect(creditApplicationEffects.getFilteredCreditApplication(id as string))
    dispatchEffect(creditApplicationEffects.getCustomerFeedback(id as string))
  }, [id])

  React.useEffect(() => {
    if (creditApplication) {
      isPollingCreditApplication.current =
        creditApplication.prequalificationDecision === null ||
        (creditApplication.status === Enum.ECreditApplicationStatus.Active &&
          creditApplication.normsDecision === null &&
          creditApplication.prequalificationDecision.decision !== Enum.EFinalDecision.Refused)

      if (isPollingCreditApplication.current && retry < MAX_RETRIES) {
        setTimeout(() => {
          dispatchEffect(creditApplicationEffects.getFilteredCreditApplication(id as string))
          setRetry(() => retry + 1)
        }, 5000)
      } else if (isPollingCreditApplication.current && retry >= MAX_RETRIES) {
        isPollingCreditApplication.current = false
        navigate(creditApplicationContainerSelection.renderComponent(id as string, null, customerFeedback))
      } else {
        navigate(creditApplicationContainerSelection.renderComponent(id as string, creditApplication, customerFeedback))
      }
    }
  }, [creditApplication, id])

  return (
    <>
      {isPollingCreditApplication.current && (
        <div>
          <Loader description="" title={t('common.withYouShortly')} />
        </div>
      )}
      <Routes>
        <Route path="review-prequalifydecision" element={<PrequalificationDecisionContainer />} />
        <Route path="review-finaldecision" element={<FinalDecisionContainer />} />
        <Route path="income-verification" element={<BankAccountInformationForm />} />
        <Route path="worksheet" element={<Worksheet />} />
        <Route path="verify-identity" element={<FailedToObtainCreditReportForm />} />
        <Route path="cancelled" element={<CreditApplicationCancelled />} />
        <Route path="completed" element={<CreditApplicationCompleted />} />
        <Route path="feedback-page" element={<FeedBackPage />} />
        <Route path="feedbackReview-page" element={<FeedBackReviewPage />} />
        <Route path="credit-report-error" element={<CreditReportError />} />
        <Route path="reportFeedback" element={<CustomerFeedbackPage />} />
        <Route path="badReview" element={<BadReviewPage />} />
      </Routes>
    </>
  )
}
